/**
 * Importi i një kontrate ekzistuese si template — njohja e vendeve bosh.
 *
 * Importi i vjetër kërkonte vetëm «[emri]», «{emri}» ose «Emri:» — forma që
 * pothuajse askush s'i përdor. Formulari zyrtar i ministrisë (Ligji 03/L-212),
 * dhe shumica e kontratave që kompanitë i kanë në Word, janë ndërtuar ndryshe:
 * një vijë bosh `________`, një etiketë para saj («duke filluar nga:») dhe një
 * shpjegim në kllapa poshtë («(emri dhe mbiemri i të punësuarit, numri personal…)»).
 * Mbi formularin zyrtar importi i vjetër gjente 0 fusha nga 19 vende bosh, dhe
 * rregulli «Emri:» e zëvendësonte etiketën në vend të vlerës.
 *
 * Këtu çdo vend bosh bëhet një «slot» me kontekstin e vet dhe një propozim:
 *   1. etiketa para vijës vendos e para (ndeshja më afër vijës fiton, që
 *      «Kohë të caktuar, duke filluar nga:» të japë datën e fillimit);
 *   2. disa etiketa e bëjnë vendin qëllimisht manual (puna provuese, gjykata,
 *      data e nënshkrimit) — aty s'ka të dhënë në sistem dhe s'propozohet asgjë;
 *   3. pa etiketë të njohur, shpjegimi në kllapa përshkruan gjithë vlerën, që
 *      mund të jetë e përbërë (emri + nr. personal + kualifikimi + adresa).
 * Përdoruesi i rishikon propozimet para se të zbatohen — asgjë nuk ndryshohet
 * pa u parë.
 */

const BLANK_RE = /_{3,}|\.{5,}|…{2,}/g;

/*
 * Vendmbajtës "kodi", që i lënë sistemet e tjera HR apo modelet e avokatëve:
 * {!first_name!}, {{start_date}}, [[salary]], ${birth_date}, <<position>>.
 * Çelësi duhet të jetë identifikues (pa hapësira) — «{{Emri}}» me fjalë të
 * lira e trajton `convertExplicitPlaceholders`.
 */
const CODE_PLACEHOLDER_RE = /\{!\s*([A-Za-z][\w.-]{0,60}?)\s*!\}|\{\{\s*([A-Za-z][\w.-]{0,60}?)\s*\}\}|\[\[\s*([A-Za-z][\w.-]{0,60}?)\s*\]\]|\$\{\s*([A-Za-z][\w.-]{0,60}?)\s*\}|(?:<<|&lt;&lt;)\s*([A-Za-z][\w.-]{0,60}?)\s*(?:>>|&gt;&gt;)/g;

/*
 * Çelësi → fusha. Fjalët e çelësit (first_name, startOfContract, date-of-birth)
 * krahasohen me rregullat në radhë; rregulli i parë që ndeshet fiton, prandaj
 * "starting_salary" kapet nga paga para se nga data e fillimit.
 */
const CODE_RULES = [
  { any: ['logo', 'stema', 'emblem'], value: '@CompanyLogo' },
  { any: ['protocol', 'protokoll', 'protokolli', 'protokollit', 'contractnumber', 'contractno'], value: '@ContractNumber' },
  { all: ['contract', 'number'], value: '@ContractNumber' },
  { all: ['contract', 'no'], value: '@ContractNumber' },
  { all: ['nr', 'kontrates'], value: '@ContractNumber' },
  { all: ['company', 'name'], value: '@CompanyName' },
  { any: ['nui', 'vat', 'businessid'], value: '@CompanyNUI' },
  { any: ['director', 'representative', 'ceo', 'drejtor', 'perfaqesues'], value: '@CompanyDirector' },
  { all: ['company', 'address'], value: '@CompanyAddress' },
  { any: ['salary', 'wage', 'pay', 'paga', 'rroga', 'gross', 'bruto'], value: '@BaseSalary' },
  { any: ['sign', 'signing', 'signingt', 'signature', 'nenshkrim', 'nenshkrimit'], value: '@SigningDate' },
  { any: ['birth', 'dob', 'birthday', 'datelindja', 'datelindje'], value: '@Birthday' },
  { all: ['first', 'name'], value: '@FirstName' },
  { any: ['firstname', 'emri', 'forename', 'given'], value: '@FirstName' },
  { all: ['last', 'name'], value: '@LastName' },
  { any: ['lastname', 'surname', 'mbiemri', 'family'], value: '@LastName' },
  { any: ['email', 'mail'], value: '@Email' },
  { any: ['phone', 'mobile', 'tel', 'telefoni'], value: '@Phone' },
  { all: ['employee', 'id'], value: '@PersonalId' },
  { any: ['parent', 'father', 'mother', 'prindit'], value: '@ParentName' },
  { all: ['full', 'name'], value: '@FirstName @LastName' },
  { any: ['fullname', 'employee', 'worker', 'punetori', 'name'], value: '@FirstName @LastName' },
  { any: ['personal', 'passport', 'nid', 'idnumber', 'jmbg', 'pasaporta', 'leternjoftim'], value: '@PersonalId' },
  { any: ['iban', 'account', 'xhirollogaria'], value: '@BankAccount' },
  { any: ['bank', 'banka'], value: '@BankName' },
  { any: ['postal', 'zip', 'postcode'], value: '@PostalCode' },
  { any: ['city', 'town', 'qyteti'], value: '@Place' },
  { any: ['country', 'shteti'], value: '@Country' },
  { any: ['address', 'residence', 'adresa', 'vendbanimi'], value: '@Address' },
  { any: ['position', 'job', 'title', 'role', 'pozita', 'occupation'], value: '@Position' },
  { any: ['department', 'departamenti', 'sector'], value: '@Department' },
  { any: ['qualification', 'education', 'kualifikimi'], value: '@Qualification' },
  { any: ['profession', 'profesioni'], value: '@Profession' },
  { any: ['gender', 'sex', 'gjinia'], value: '@Gender' },
  { any: ['start', 'starting', 'begin', 'commencement', 'fillimit'], value: '@StartDate' },
  { any: ['end', 'expiry', 'expiration', 'until', 'mbarimit', 'perfundimit'], value: '@EndDate' },
  { all: ['contract', 'type'], value: '@ContractType' },
  { all: ['work', 'type'], value: '@WorkType' },
  { any: ['today', 'date', 'data'], value: '@SigningDate' },
];

const keyWords = (key) => String(key || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .toLowerCase()
  .replace(/[ëé]/g, 'e').replace(/ç/g, 'c')
  .split(/[^a-z0-9]+/)
  .filter(Boolean);

/** `first_name` → `@FirstName`; '' kur çelësi s'njihet. */
export function codeKeyToField(key) {
  const words = keyWords(key);
  const joined = words.join('');
  const hit = CODE_RULES.find((r) => (r.all
    ? r.all.every((w) => words.includes(w))
    : r.any.some((w) => words.includes(w) || joined === w)));
  return hit ? hit.value : '';
}

// Kontekst që tregon se fjala «emri»/«adresa» i përket punëdhënësit.
const COMPANY_CONTEXT_RE = /punëdhënës|punedhenes|biznes|kompani|shoqëri|shoqeri|firm[aë]\b|\bseli/i;

// Vende që formulari i lë qëllimisht për t'u plotësuar me dorë.
const MANUAL_RE = /provues|shpenzim|udhëtim|udhetim|gjykat|nënshkrua|nenshkrua|kopje|\borë\b|\bore\b|nënshkrim/i;

const WORKER_RULES = [
  { re: /emri dhe mbiemri|emri e mbiemri|emri i plotë|emër e mbiemër|emri,? mbiemri/i, value: '@FirstName @LastName' },
  { re: /emri i prindit|emri prindit/i, value: '@ParentName' },
  { re: /përfaqëson|perfaqeson|përfaqësues|perfaqesues/i, value: '@CompanyDirector' },
  { re: /mbiemr/i, value: '@LastName' },
  { re: /\bemri\b|\bemër\b/i, value: '@FirstName' },
  { re: /numri personal|nr\.? ?personal|letërnjoftim|leternjoftim|pasaport/i, value: '@PersonalId', prefix: 'nr. personal ' },
  { re: /datëlindj|datelindj|data e lindjes|lindur më/i, value: '@Birthday' },
  { re: /kualifikim|arsimi|përgatitja profesionale/i, value: '@Qualification' },
  { re: /profesion/i, value: '@Profession' },
  { re: /\bdetyra\b|\bdetyrat\b|pozit[aëe]\b|vendi i punës që|thirrj/i, value: '@Profession' },
  { re: /departament|sektor/i, value: '@Department' },
  { re: /punët në|punet ne|vendi i punës|vend pune|vendi i punes/i, value: '@CompanyAddress' },
  { re: /adres|vendbanim|me banim/i, value: '@Address', prefix: 'adresa ' },
  { re: /telefon|\btel\b/i, value: '@Phone' },
  { re: /e-?mail/i, value: '@Email' },
  { re: /xhirollogari|llogari bankare|\biban\b/i, value: '@BankAccount' },
  { re: /\bbank/i, value: '@BankName' },
  { re: /gjini/i, value: '@Gender' },
  { re: /statusi martesor/i, value: '@MaritalStatus' },
  { re: /data e fillimit|duke filluar|fillon më|fillojë punën|filloj[eë] pun/i, value: '@StartDate' },
  { re: /data e mbarimit|deri më|deri me|mbaron|përfundon/i, value: '@EndDate' },
  { re: /lloji i kontrat|kohë të caktuar|kohë të pacaktuar/i, value: '@ContractType' },
  { re: /lloji i punës|lloji i punësimit|orar të plotë|orar jo të plotë/i, value: '@WorkType' },
  { re: /\bpag[aëe]\b|paga bazë|rrog/i, value: '@BaseSalary' },
];

const COMPANY_RULES = [
  { re: /emri\/emërtimi|emërtimi|emertimi|emri i (punëdhënësit|kompanisë|biznesit)|\bemri\b|\bfirma/i, value: '@CompanyName' },
  { re: /\bseli|adres/i, value: '@CompanyAddress', prefix: 'me seli në ' },
  { re: /numri i regjistrimit|\bnui\b|numri unik/i, value: '@CompanyNUI', prefix: 'NUI ' },
  { re: /numri fiskal/i, value: '@CompanyFiscalNumber', prefix: 'nr. fiskal ' },
  { re: /përfaqës|perfaqes|drejtor/i, value: '@CompanyDirector' },
  { re: /qytet|komun/i, value: '@CompanyCity' },
  { re: /telefon/i, value: '@CompanyPhone' },
  { re: /e-?mail/i, value: '@CompanyEmail' },
];

// Kllapa që janë pjesë e tekstit ligjor, jo udhëzim për plotësim.
const LEGAL_PAREN_RE = /^(në|ne) tekstin|^(në|ne) vijim|^më tej|^me tej/i;

const stripTags = (s) => String(s || '')
  .replace(/<\/(p|div|h[1-6]|li|tr|td|th)>|<br\s*\/?>/gi, '\n')
  .replace(/<[^>]+>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&');

/** Ndeshja më afër fundit të etiketës — ajo që qëndron ngjitur me vijën. */
function matchLabel(label) {
  let best = null;
  WORKER_RULES.forEach((r) => {
    const re = new RegExp(r.re.source, 'gi');
    let m;
    while ((m = re.exec(label))) {
      const end = m.index + m[0].length;
      if (!best || end > best.end) best = { end, value: r.value };
    }
  });
  return best ? best.value : '';
}

/** Gjithë fushat që përmend një shpjegim, sipas renditjes në tekst. */
function matchHint(hint) {
  const rules = COMPANY_CONTEXT_RE.test(hint) ? COMPANY_RULES : WORKER_RULES;
  const taken = [];
  const found = [];
  rules.forEach((r) => {
    const re = new RegExp(r.re.source, 'gi');
    let m;
    while ((m = re.exec(hint))) {
      const a = m.index; const b = a + m[0].length;
      if (taken.some(([x, y]) => a < y && b > x)) continue;
      taken.push([a, b]);
      if (!found.some((f) => f.value === r.value)) found.push({ at: a, value: r.value, prefix: r.prefix || '' });
    }
  });
  found.sort((x, y) => x.at - y.at);
  if (found.length === 1) return found[0].value;
  return found.map((f, i) => (i === 0 ? f.value : f.prefix + f.value)).join(', ');
}

/** «[Emri]», «{Emri}», «{{Emri}}», ««Emri»» → @FirstName, kur emri njihet. */
export function convertExplicitPlaceholders(html) {
  return String(html || '').replace(
    /(\{\{|\[|\{|«|&lt;&lt;)\s*([A-Za-zÀ-žëËçÇ ./-]{2,40}?)\s*(\}\}|\]|\}|»|&gt;&gt;)/g,
    (m, _o, label) => {
      const v = matchHint(label);
      return v && !v.includes(',') ? v : m;
    },
  );
}

/**
 * Gjen vendet bosh dhe propozon një fushë për secilin.
 * Kthen { html, slots }: `html` me vendmbajtësit eksplicitë tashmë të kthyer,
 * `slots` me pozicionet brenda atij html-i — për `applySlots`.
 */
export function detectSlots(rawHtml) {
  const html = convertExplicitPlaceholders(rawHtml);
  const blanks = [];
  let m;
  const insideTag = (at) => html.lastIndexOf('<', at) > html.lastIndexOf('>', at);
  const re = new RegExp(BLANK_RE.source, 'g');
  while ((m = re.exec(html))) {
    // Pikat brenda një atributi apo URL-je s'janë vend bosh.
    if (insideTag(m.index)) continue;
    blanks.push({ start: m.index, end: m.index + m[0].length });
  }
  const codeRe = new RegExp(CODE_PLACEHOLDER_RE.source, 'g');
  while ((m = codeRe.exec(html))) {
    if (insideTag(m.index)) continue;
    const key = m.slice(1).find(Boolean);
    blanks.push({ start: m.index, end: m.index + m[0].length, key });
  }
  blanks.sort((a, b) => a.start - b.start);

  const slots = blanks.map((b, i) => {
    const prevEnd = i > 0 ? blanks[i - 1].end : Math.max(0, b.start - 600);
    const nextStart = i < blanks.length - 1 ? blanks[i + 1].start : Math.min(html.length, b.end + 600);

    // Etiketa: fjalia e fundit para vijës. Kur vija nis rreshtin (Word-i e ka
    // thyer paragrafin), etiketa është në rreshtin e mëparshëm.
    const lines = stripTags(html.slice(prevEnd, b.start)).split('\n');
    let line = lines.pop();
    while (!line.replace(/[\s.,;:)]/g, '') && lines.length) line = lines.pop();
    const label = line.split(/[.;!?](?=\s)/).pop().replace(/\s+/g, ' ').trim().slice(-90);

    // Shpjegimi: kllapat menjëherë pas vijës, edhe kur kalojnë në rreshtin tjetër.
    // Pa kllapë mbyllëse (formulari zyrtar ka një të tillë), shpjegimi mbaron
    // me rreshtin e vet.
    const afterRaw = html.slice(b.end, nextStart);
    let hint = '';
    let hintRange = null;
    if (/^[\s,.;:]*\(/.test(stripTags(afterRaw))) {
      const open = afterRaw.indexOf('(');
      const close = afterRaw.indexOf(')', open);
      const nextOpen = afterRaw.indexOf('(', open + 1);
      const lineEnd = afterRaw.indexOf('<', open);
      const end = close >= 0 && close - open < 400 && (nextOpen < 0 || close < nextOpen)
        ? close + 1
        : (lineEnd >= 0 ? lineEnd : afterRaw.length);
      const text = stripTags(afterRaw.slice(open, end)).replace(/^\(|\)$/g, '').replace(/\s+/g, ' ').trim();
      if (text.length >= 3 && !LEGAL_PAREN_RE.test(text)) {
        hint = text;
        hintRange = { start: b.end + open, end: b.end + end };
      }
    }

    let suggestion = '';
    let reason = '';
    if (b.key) {
      // Vendmbajtësi e thotë vetë çfarë është — s'ka nevojë për kontekst.
      suggestion = codeKeyToField(b.key);
      reason = suggestion ? 'placeholder' : 'unknown';
      return { id: i, start: b.start, end: b.end, blank: html.slice(b.start, b.end), placeholder: html.slice(b.start, b.end), key: b.key, label, hint: '', hintRange: null, suggestion, reason };
    }
    if (MANUAL_RE.test(label)) {
      reason = 'manual';
    } else {
      suggestion = matchLabel(label);
      if (suggestion) reason = 'label';
      else if (hint) {
        suggestion = matchHint(hint);
        if (suggestion) reason = 'hint';
      }
    }

    return { id: i, start: b.start, end: b.end, blank: html.slice(b.start, b.end), label, hint, hintRange, suggestion, reason };
  });

  return { html, slots };
}

/**
 * Zbaton zgjedhjet: `choices[id]` është teksti që zë vendin bosh ('' = lëre bosh).
 * Shpjegimet në kllapa hiqen vetëm aty ku vendi u plotësua — ku mbetet bosh,
 * shpjegimi i duhet ende atij që e plotëson me dorë.
 */
export function applySlots(html, slots, choices, { stripHints = true } = {}) {
  let out = html;
  [...slots].sort((a, b) => b.start - a.start).forEach((s) => {
    const value = choices[s.id];
    if (!value) return;
    if (stripHints && s.hintRange) {
      out = out.slice(0, s.hintRange.start) + out.slice(s.hintRange.end);
    }
    out = out.slice(0, s.start) + value + out.slice(s.end);
  });
  // Paragrafë që mbetën bosh pasi u hoq shpjegimi.
  return out.replace(/<p[^>]*>(\s|&nbsp;)*[.,;]?(\s|&nbsp;)*<\/p>\s*/gi, '');
}

/** Emri i template-it nga emri i skedarit: «Kontratë-pune_2024.docx» → «Kontratë pune 2024». */
export function nameFromFile(fileName) {
  return String(fileName || '')
    .replace(/\.(docx?|pdf)$/i, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
